import React, { useState, useEffect, useRef } from 'react';
import {
  Rocket, X, Check, Loader2,
  AlertCircle, CheckCircle2,
  Sparkles, AlertTriangle,
  ArrowLeft, ArrowRight, Plus, Trash2,
  Target, Layers, ChevronDown, Lock, Library, Globe
} from 'lucide-react';
import { runCampaignWizard, fetchOfferContent } from '../lib/campaignPipeline';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { parseHtmlFormEmbed } from '../utils/formParser';

// ─── Constants ────────────────────────────────────────────────────────────────

const TRAFFIC_SOURCES = [
  {
    id: 'mgid', emoji: '🟧', label: 'MGID', sub: 'MGID Native Discovery ({click_id})', color: '#f97316',
    clickIdMacro: '{click_id}',
    urlParamsTemplate: 'source=mgid&campaign={campaign_id}&angle={teaser_id}&click_id={click_id}&widget_id={widget_id}',
    angles: [
      { id: 'doctor_discovery', label: 'Doctor Discovery',     desc: 'Hidden secret revealed by a rogue doctor',        funnel: 'adv_capture' },
      { id: 'news_report',      label: 'Breaking News Report', desc: 'Shocking news-style editorial with urgent reveal',  funnel: 'adv_capture' },
      { id: 'success_story',    label: 'Success Story',        desc: 'Real person shares transformation journey',         funnel: 'adv_capture' },
      { id: 'listicle_review',  label: 'Listicle / Top 5',    desc: 'Multi-offer review and comparison editorial',       funnel: 'listicle'    },
    ]
  },
  {
    id: 'taboola', emoji: '🟦', label: 'Taboola', sub: 'Taboola Native Discovery ({click_id})', color: '#3b82f6',
    clickIdMacro: '{click_id}',
    urlParamsTemplate: 'source=taboola&campaign={campaign_id}&angle={campaign_item_id}&click_id={click_id}&tblci={click_id}&site={site}',
    angles: [
      { id: 'doctor_discovery', label: 'Doctor Discovery',     desc: 'Hidden secret revealed by a rogue doctor',        funnel: 'adv_capture' },
      { id: 'news_report',      label: 'Breaking News Report', desc: 'Shocking news-style editorial with urgent reveal',  funnel: 'adv_capture' },
      { id: 'success_story',    label: 'Success Story',        desc: 'Real person shares transformation journey',         funnel: 'adv_capture' },
      { id: 'listicle_review',  label: 'Listicle / Top 5',    desc: 'Multi-offer review and comparison editorial',       funnel: 'listicle'    },
    ]
  },
  {
    id: 'outbrain', emoji: '🟪', label: 'Outbrain', sub: 'Outbrain Native Ads ({{ob_click_id}})', color: '#a78bfa',
    clickIdMacro: '{{ob_click_id}}',
    urlParamsTemplate: 'source=outbrain&campaign={{campaign_id}}&angle={{promoted_link_id}}&click_id={{ob_click_id}}&ob_click_id={{ob_click_id}}&section={{section_id}}',
    angles: [
      { id: 'doctor_discovery', label: 'Doctor Discovery',     desc: 'Hidden secret revealed by a rogue doctor',        funnel: 'adv_capture' },
      { id: 'news_report',      label: 'Breaking News Report', desc: 'Shocking news-style editorial with urgent reveal',  funnel: 'adv_capture' },
      { id: 'success_story',    label: 'Success Story',        desc: 'Real person shares transformation journey',         funnel: 'adv_capture' },
      { id: 'listicle_review',  label: 'Listicle / Top 5',    desc: 'Multi-offer review and comparison editorial',       funnel: 'listicle'    },
    ]
  },
  {
    id: 'facebook', emoji: '📘', label: 'Facebook / Meta', sub: 'FB Ads / Instagram Ads ({{fbclid}})', color: '#60a5fa',
    clickIdMacro: '{{fbclid}}',
    urlParamsTemplate: 'source=facebook&campaign={{campaign.name}}&angle={{ad.name}}&click_id={{fbclid}}',
    angles: [
      { id: 'vsl_curiosity',   label: 'VSL Curiosity Hook',   desc: 'Video pre-sell that teases a shocking secret',         funnel: 'vsl_offer'    },
      { id: 'emotional_story', label: 'Emotional Story',      desc: 'Relatable before/after journey video bridge',           funnel: 'vsl_offer'    },
      { id: 'lead_gen',        label: 'Lead Generation',      desc: 'Capture email first, send to offer via autoresponder',  funnel: 'squeeze_offer' },
      { id: 'adv_presell',     label: 'Advertorial Pre-sell', desc: 'Article-style pre-sell before the offer',              funnel: 'adv_offer'    },
    ]
  },
  {
    id: 'google', emoji: '🔍', label: 'Google Search', sub: 'Google Ads / Bing Ads ({gclid})', color: '#34d399',
    clickIdMacro: '{gclid}',
    urlParamsTemplate: 'source=google&campaign={campaignid}&angle={creative}&click_id={gclid}&gclid={gclid}',
    angles: [
      { id: 'honest_review', label: 'Honest Review',   desc: 'Detailed does-it-work product review page',     funnel: 'review_offer' },
      { id: 'comparison',    label: 'Top Comparison',  desc: 'Side-by-side comparison of competing products',  funnel: 'listicle'     },
      { id: 'case_study',    label: 'Case Study',      desc: 'In-depth research/result-based presell page',    funnel: 'adv_offer'    },
      { id: 'direct_bridge', label: 'Direct Bridge',   desc: 'Simple problem/solution bridge to offer',        funnel: 'bridge_offer' },
    ]
  },
  {
    id: 'email', emoji: '📧', label: 'Email / Solo Ads', sub: 'Solo Ads / Newsletter', color: '#fb923c',
    clickIdMacro: '{subid}',
    urlParamsTemplate: 'source=email&campaign={campaign}&angle={angle}&click_id={subid}',
    angles: [
      { id: 'personal_rec',   label: 'Personal Recommendation', desc: 'Warm personal endorsement bridge page',       funnel: 'bridge_offer'  },
      { id: 'exclusive_deal', label: 'Exclusive Deal Page',     desc: 'Limited-time bonus offer landing page',       funnel: 'bridge_offer'  },
      { id: 'lead_capture',   label: 'Lead Capture First',      desc: 'Collect email before forwarding to offer',    funnel: 'squeeze_offer' },
      { id: 'adv_warmup',     label: 'Advertorial Warm-Up',     desc: 'Editorial pre-sell before sending to offer',  funnel: 'adv_offer'    },
    ]
  },
  {
    id: 'youtube', emoji: '🎥', label: 'YouTube / Video', sub: 'YouTube Ads / Organic ({gclid})', color: '#f87171',
    clickIdMacro: '{gclid}',
    urlParamsTemplate: 'source=youtube&campaign={campaignid}&angle={creative}&click_id={gclid}',
    angles: [
      { id: 'video_review',  label: 'Video Review Bridge', desc: 'Review-style VSL bridge page under the video',  funnel: 'vsl_offer'    },
      { id: 'tutorial_demo', label: 'Tutorial / Demo',     desc: 'How-it-works page with CTA to offer',           funnel: 'bridge_offer' },
      { id: 'vsl_presell',   label: 'VSL Pre-sell',        desc: 'Full video sales letter leading to offer',      funnel: 'vsl_capture'  },
    ]
  },
  {
    id: 'push', emoji: '🔔', label: 'Push / Pop Ads', sub: 'PropellerAds / RichPush / Adcash', color: '#e879f9',
    clickIdMacro: '{clickid}',
    urlParamsTemplate: 'source=push&campaign={campaignid}&angle={bannerid}&click_id={clickid}',
    angles: [
      { id: 'shock_hook',    label: 'Shock Hook Landing',    desc: 'Bold 1-page shock hook pre-sell before the offer',    funnel: 'bridge_offer'  },
      { id: 'curiosity_adv', label: 'Curiosity Advertorial', desc: 'Short "you won\'t believe this" editorial',             funnel: 'adv_offer'     },
      { id: 'quiz_presell',  label: 'Quiz / Survey Funnel',  desc: 'Engagement quiz that segments and personalizes',        funnel: 'quiz_offer'    },
      { id: 'simple_bridge', label: 'Minimalist Bridge Page', desc: 'Ultra-fast loading single-screen bridge',              funnel: 'bridge_offer'  },
    ]
  },
];

const FUNNEL_TYPES = [
  'Optin / Squeeze Page',
  'Lead Magnet Funnel',
  'Minimalist Bridge',
  'Curiosity Advertorial',
  'Shock Hook',
  'Quiz Funnel',
  'VSL Presell',
  'Review / Listicle',
  'Custom HTML (Paste Code)',
  'External Page (Self-Hosted)',
];

const FUNNEL_TYPE_COLORS = {
  'Optin / Squeeze Page': { bg: '#a855f7', text: '#6b21a8' },
  'Lead Magnet Funnel':   { bg: '#8b5cf6', text: '#5b21b6' },
  'Minimalist Bridge':   { bg: '#06b6d4', text: '#0e7490' },
  'Curiosity Advertorial': { bg: '#a78bfa', text: '#6d28d9' },
  'Shock Hook':          { bg: '#f87171', text: '#b91c1c' },
  'Quiz Funnel':         { bg: '#ec4899', text: '#9d174d' },
  'VSL Presell':         { bg: '#818cf8', text: '#4338ca' },
  'Review / Listicle':   { bg: '#34d399', text: '#065f46' },
  'Custom HTML (Paste Code)': { bg: '#10b981', text: '#065f46' },
  'External Page (Self-Hosted)': { bg: '#f59e0b', text: '#92400e' },
};

// ─── Angle Count Recommendation Logic ────────────────────────────────────────

function getAngleRecommendation(payoutStr, competitionLevel = 'medium') {
  const payout = parseFloat((payoutStr || '').replace(/[^0-9.]/g, '')) || 0;

  if (payout < 5) {
    return {
      max: 2,
      suggested: 2,
      reason: `Low payout ($${payout.toFixed(2)}) — keep testing costs minimal. Plan 2 tight angles max.`,
      tier: 'low',
    };
  }
  if (payout <= 20) {
    const isLowComp = competitionLevel === 'low';
    return {
      max: isLowComp ? 4 : 3,
      suggested: isLowComp ? 3 : 3,
      reason: `Medium payout ($${payout.toFixed(2)}) with ${competitionLevel} competition — ${isLowComp ? '3–4 angles gives strong split-test coverage' : '3 angles balances testing cost vs payout'}.`,
      tier: 'medium',
    };
  }
  return {
    max: 5,
    suggested: 3,
    reason: `High payout ($${payout.toFixed(2)}) — up to 5 angles justified. Higher commissions absorb aggressive split-testing budgets.`,
    tier: 'high',
  };
}

function deriveCompetitionLevel(coachResult) {
  if (!coachResult) return 'medium';
  const text = JSON.stringify(coachResult).toLowerCase();
  if (text.includes('very competitive') || text.includes('highly competitive') || text.includes('saturated')) return 'high';
  if (text.includes('low competition') || text.includes('blue ocean') || text.includes('untapped')) return 'low';
  return 'medium';
}

// ─── Static Mini Funnel Map (SVG) ─────────────────────────────────────────────

function StaticFunnelMap({ trafficSource, anglePlans, offerName }) {
  if (!trafficSource || !anglePlans || anglePlans.length === 0) return null;

  const nodeW = 160;
  const nodeH = 80;
  const colGap = 200;
  const rowGap = 100;
  const padX = 40;
  const padY = 40;
  const n = anglePlans.length;
  const totalH = n * nodeH + (n - 1) * (rowGap - nodeH) + padY * 2;
  const svgH = Math.max(totalH, 200);
  const svgW = nodeW * 3 + colGap * 2 + padX * 2;

  const trafficX = padX;
  const trafficY = svgH / 2 - nodeH / 2;
  const offerX = padX + nodeW * 2 + colGap * 2;
  const offerY = svgH / 2 - nodeH / 2;

  const angleXs = Array(n).fill(padX + nodeW + colGap);
  const angleYs = Array.from({ length: n }, (_, i) => {
    const blockH = n * nodeH + (n - 1) * (rowGap - nodeH);
    const startY = svgH / 2 - blockH / 2;
    return startY + i * rowGap;
  });

  const srcCX = trafficX + nodeW;
  const srcCY = trafficY + nodeH / 2;
  const dstCX = offerX;
  const dstCY = offerY + nodeH / 2;

  return (
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
      <svg width={svgW} height={svgH} style={{ display: 'block', minWidth: svgW }}>
        <defs>
          <filter id="glow-orange">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#4f46e5" opacity="0.7" />
          </marker>
          <style>{`
            @keyframes dashMove { to { stroke-dashoffset: -20; } }
            .dash-line { animation: dashMove 1.5s linear infinite; }
          `}</style>
        </defs>

        {/* Traffic → Angle paths */}
        {angleYs.map((ay, i) => {
          const angCY = ay + nodeH / 2;
          const mid1X = srcCX + (angleXs[i] - srcCX) * 0.45;
          return (
            <path
              key={`t-a-${i}`}
              className="dash-line"
              d={`M ${srcCX} ${srcCY} C ${mid1X} ${srcCY}, ${mid1X} ${angCY}, ${angleXs[i]} ${angCY}`}
              fill="none"
              stroke="#4f46e5"
              strokeWidth="2"
              strokeDasharray="6 4"
              opacity="0.6"
              markerEnd="url(#arrow)"
            />
          );
        })}

        {/* Angle → Offer paths */}
        {angleYs.map((ay, i) => {
          const angCY = ay + nodeH / 2;
          const angRX = angleXs[i] + nodeW;
          const mid2X = angRX + (dstCX - angRX) * 0.55;
          return (
            <path
              key={`a-o-${i}`}
              className="dash-line"
              d={`M ${angRX} ${angCY} C ${mid2X} ${angCY}, ${mid2X} ${dstCY}, ${dstCX} ${dstCY}`}
              fill="none"
              stroke="#f97316"
              strokeWidth="2"
              strokeDasharray="6 4"
              opacity="0.5"
              markerEnd="url(#arrow)"
            />
          );
        })}

        {/* Traffic Source Node */}
        <g>
          <rect x={trafficX} y={trafficY} width={nodeW} height={nodeH} rx="10"
            fill="#13161d" stroke={trafficSource.color || '#22c55e'} strokeWidth="2" />
          <text x={trafficX + nodeW / 2} y={trafficY + 22} textAnchor="middle"
            fontSize="18" dominantBaseline="middle">{trafficSource.emoji}</text>
          <text x={trafficX + nodeW / 2} y={trafficY + 44} textAnchor="middle"
            fill="#ffffff" fontSize="10" fontWeight="bold" fontFamily="monospace">
            {trafficSource.label.length > 16 ? trafficSource.label.slice(0, 14) + '…' : trafficSource.label}
          </text>
          <text x={trafficX + nodeW / 2} y={trafficY + 60} textAnchor="middle"
            fill="#64748b" fontSize="8" fontFamily="monospace">TRAFFIC SOURCE</text>
        </g>

        {/* Angle Nodes */}
        {anglePlans.map((angle, i) => {
          const ax = angleXs[i];
          const ay = angleYs[i];
          const ftColor = FUNNEL_TYPE_COLORS[angle.funnelType] || { bg: '#818cf8', text: '#4338ca' };
          return (
            <g key={angle.id}>
              <rect x={ax} y={ay} width={nodeW} height={nodeH} rx="10"
                fill="#13161d" stroke="#f9731630" strokeWidth="1.5" />
              {/* Angle name */}
              <text x={ax + nodeW / 2} y={ay + 20} textAnchor="middle"
                fill="#ffffff" fontSize="9" fontWeight="bold" fontFamily="monospace">
                {(angle.name || `Angle ${i + 1}`).length > 18
                  ? (angle.name || `Angle ${i + 1}`).slice(0, 16) + '…'
                  : (angle.name || `Angle ${i + 1}`)}
              </text>
              {/* Funnel type pill */}
              <rect x={ax + 8} y={ay + 32} width={nodeW - 16} height={16} rx="8"
                fill={ftColor.bg + '22'} />
              <text x={ax + nodeW / 2} y={ay + 41} textAnchor="middle"
                fill={ftColor.bg} fontSize="7.5" fontFamily="monospace" fontWeight="bold">
                {(angle.funnelType || 'Funnel Type').length > 20
                  ? (angle.funnelType || 'Funnel Type').slice(0, 18) + '…'
                  : (angle.funnelType || 'Funnel Type')}
              </text>
              {/* Queued badge */}
              <rect x={ax + nodeW / 2 - 24} y={ay + 55} width={48} height={14} rx="7"
                fill="#eab30830" />
              <text x={ax + nodeW / 2} y={ay + 63} textAnchor="middle"
                fill="#eab308" fontSize="7" fontFamily="monospace" fontWeight="bold">🟡 QUEUED</text>
            </g>
          );
        })}

        {/* Offer Node */}
        <g>
          <rect x={offerX} y={offerY} width={nodeW} height={nodeH} rx="10"
            fill="#13161d" stroke="#fbbf24" strokeWidth="2"
            filter="url(#glow-orange)" />
          <text x={offerX + nodeW / 2} y={offerY + 22} textAnchor="middle"
            fontSize="18" dominantBaseline="middle">💰</text>
          <text x={offerX + nodeW / 2} y={offerY + 44} textAnchor="middle"
            fill="#fbbf24" fontSize="10" fontWeight="bold" fontFamily="monospace">
            {(offerName || 'Affiliate Offer').length > 16
              ? (offerName || 'Affiliate Offer').slice(0, 14) + '…'
              : (offerName || 'Affiliate Offer')}
          </text>
          <text x={offerX + nodeW / 2} y={offerY + 60} textAnchor="middle"
            fill="#64748b" fontSize="8" fontFamily="monospace">OFFER (SHARED)</text>
        </g>
      </svg>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CampaignWizard({
  isOpen, onClose, campaignData, setCampaignData, userProfile, onLaunchSuccess
}) {
  // Wizard stage: 1=Offer Details, 2=Coach Analysis, 3=Traffic Source, 4=Angle Planning, 5=Funnel Map
  const [wizardStage, setWizardStage] = useState(1);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: campaignData?.name || '',
    network: campaignData?.network || 'JVZoo',
    customNetwork: campaignData?.customNetwork || '',
    affiliateLink: campaignData?.affiliateLink || '',
    niche: campaignData?.niche || '',
    offer_preview_url: campaignData?.offer_preview_url || '',
    offer_description: campaignData?.offer_description || campaignData?.niche || '',
    payout: campaignData?.payout || '$47.00',
    payoutModel: campaignData?.payoutModel || 'CPS/Sale',
    networkEpc: campaignData?.networkEpc || '',
    targetGeo: campaignData?.targetGeo || 'US, UK, CA',
    customDomain: campaignData?.customDomain || '',
    collectEmails: campaignData?.collectEmails !== undefined ? campaignData.collectEmails : true,
    autoresponderProvider: campaignData?.autoresponderProvider || (campaignData?.customForm ? 'custom_html' : 'mailerxp'), // 'mailerxp' | 'custom_html'
    mailerXpList: campaignData?.mailerXpList || '',
    mailerXpSegment: campaignData?.mailerXpSegment || '',
    customForm: campaignData?.customForm || null,
    customFormHtml: campaignData?.customForm?.rawHtml || '',
  });

  // Step 2 (Paid-traffic readiness): Fetch connected domains from Settings (/api/settings/domains)
  // and preselect the user's default domain (e.g., go.moneyxps.com) for new campaigns while preserving existing campaigns' domain.
  const [connectedDomains, setConnectedDomains] = useState([]);
  const [defaultSettingsDomain, setDefaultSettingsDomain] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const waitForUser = () => {
      const authInst = getAuth();
      if (authInst.currentUser) return Promise.resolve(authInst.currentUser);
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          unsub();
          resolve(authInst.currentUser || null);
        }, 4000);
        const unsub = onAuthStateChanged(authInst, (u) => {
          clearTimeout(timer);
          unsub();
          resolve(u || null);
        });
      });
    };
    const loadSettingsDomains = async () => {
      try {
        const user = await waitForUser();
        const token = user ? await user.getIdToken().catch(() => null) : null;
        if (!token || cancelled) return;
        const res = await fetch('/api/settings/domains', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const domainRecords = Array.isArray(data.domains) ? data.domains : [];
        if (cancelled) return;
        setConnectedDomains(domainRecords);
        const defDoc = domainRecords.find(d => d.isDefault) || domainRecords.find(d => d.status === 'active') || domainRecords[0];
        if (defDoc?.hostname) {
          setDefaultSettingsDomain(defDoc.hostname);
          setForm(prev => {
            // Existing campaign keeps its current domain; new campaign preselects default domain from Settings
            if (campaignData?.id && campaignData?.customDomain) {
              return prev;
            }
            if (!prev.customDomain || prev.customDomain === 'xpgo.link') {
              return { ...prev, customDomain: defDoc.hostname };
            }
            return prev;
          });
        }
      } catch (err) {
        console.warn('Failed to load settings domains in wizard:', err);
      }
    };
    loadSettingsDomains();
    return () => { cancelled = true; };
  }, [isOpen, campaignData?.id, campaignData?.customDomain]);

  const userCustomDomains = connectedDomains.length > 0
    ? connectedDomains.map(d => d.hostname)
    : (Array.isArray(userProfile?.customDomains) ? userProfile.customDomains : []);

  // MailerXP Integration state
  const [mailerXpLists, setMailerXpLists] = useState([]);
  const [loadingMailerXp, setLoadingMailerXp] = useState(false);
  const [mailerXpError, setMailerXpError] = useState(null);
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [createListError, setCreateListError] = useState(null);
  const [inlineApiKey, setInlineApiKey] = useState(userProfile?.mailerXpApi || '');
  const [savingApiKey, setSavingApiKey] = useState(false);

  // Custom Autoresponder (AWeber, GetResponse, etc.) Embed State
  const [customFormError, setCustomFormError] = useState(null);
  const [parsedFormDetails, setParsedFormDetails] = useState(campaignData?.customForm || null);

  const handleParseCustomFormHtml = (rawHtml) => {
    if (!rawHtml || !rawHtml.trim()) {
      setParsedFormDetails(null);
      setCustomFormError(null);
      setForm(p => ({ ...p, customForm: null, customFormHtml: '' }));
      return;
    }
    const result = parseHtmlFormEmbed(rawHtml);
    if (result.error) {
      setCustomFormError(result.error);
      setParsedFormDetails(null);
    } else {
      setCustomFormError(null);
      setParsedFormDetails(result);
      setForm(p => ({
        ...p,
        customForm: result,
        customFormHtml: rawHtml
      }));
    }
  };

  // Step 2
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [pipelineError, setPipelineError] = useState(null);
  const [coachResult, setCoachResult] = useState(null);
  const [showManualDesc, setShowManualDesc] = useState(!!(campaignData?.offer_description || campaignData?.niche));

  // Step 3
  const [selectedTrafficSource, setSelectedTrafficSource] = useState(null);

  // Step 4 — Angle plans
  const [anglePlans, setAnglePlans] = useState([]);
  const [angleRec, setAngleRec] = useState(null); // { max, suggested, reason, tier }

  // Save state
  const [savingCampaign, setSavingCampaign] = useState(false);

  // Historical performance from user's past campaigns
  const [accountHistory, setAccountHistory] = useState({ sources: {}, angles: {}, totalCampaigns: 0 });

  // ── Task 1/3: Saved offers (first-class entity) ────────────────────────────
  const [savedOffers, setSavedOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [needsNetworkConfirmOffer, setNeedsNetworkConfirmOffer] = useState(null);

  // Fetch and aggregate historical campaign performance on open
  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/campaigns')
      .then(r => r.json())
      .then(data => {
        const campaigns = Array.isArray(data) ? data : (data.campaigns || []);
        const sources = {};
        const angles  = {};
        campaigns.forEach(c => {
          const src = c.trafficSource;
          const ang = c.angle;
          const clicks = c.clicks || 0;
          const convs  = c.conversions || 0;
          const rev    = c.revenue || 0;
          const cr  = clicks > 0 ? convs / clicks : 0;
          const epc = clicks > 0 ? rev / clicks : 0;
          if (src && clicks >= 50) {
            if (!sources[src]) sources[src] = { count: 0, totalCR: 0, totalEPC: 0, totalClicks: 0 };
            sources[src].count++;
            sources[src].totalCR      += cr;
            sources[src].totalEPC     += epc;
            sources[src].totalClicks  += clicks;
          }
          if (ang && clicks >= 50) {
            if (!angles[ang]) angles[ang] = { count: 0, totalCR: 0, totalEPC: 0, totalClicks: 0 };
            angles[ang].count++;
            angles[ang].totalCR     += cr;
            angles[ang].totalEPC    += epc;
            angles[ang].totalClicks += clicks;
          }
        });
        Object.keys(sources).forEach(k => {
          const s = sources[k];
          s.avgCR  = s.count > 0 ? s.totalCR  / s.count : 0;
          s.avgEPC = s.count > 0 ? s.totalEPC / s.count : 0;
        });
        Object.keys(angles).forEach(k => {
          const a = angles[k];
          a.avgCR  = a.count > 0 ? a.totalCR  / a.count : 0;
          a.avgEPC = a.count > 0 ? a.totalEPC / a.count : 0;
        });
        setAccountHistory({ sources, angles, totalCampaigns: campaigns.length });
      })
      .catch(() => {});
  }, [isOpen]);

  // ── Task 1/3: Fetch saved offers on open ──────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const fetchOffers = async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken();
        const res = await fetch('/api/offers', {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
        });
        if (res.ok) {
          const data = await res.json();
          setSavedOffers(Array.isArray(data) ? data : []);
        }
      } catch { /* silently ignore — offers are optional enhancement */ }
    };
    fetchOffers();
  }, [isOpen]);

  // Fetch MailerXP lists
  const fetchMailerXpLists = async (overrideKey) => {
    const key = overrideKey || userProfile?.mailerXpApi || inlineApiKey;
    if (!key) return;
    setLoadingMailerXp(true);
    setMailerXpError(null);
    try {
      const res = await fetch('/api/mailerxp/lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: key })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to fetch lists');
      }
      const lists = data.lists || [];
      setMailerXpLists(lists);
      if (lists.length > 0) {
        setForm(p => ({
          ...p,
          mailerXpList: p.mailerXpList && lists.some(l => l.id === p.mailerXpList) ? p.mailerXpList : lists[0].id
        }));
      }
    } catch (err) {
      setMailerXpError(err.message);
    } finally {
      setLoadingMailerXp(false);
    }
  };

  useEffect(() => {
    if (isOpen && (userProfile?.mailerXpApi || inlineApiKey)) {
      fetchMailerXpLists();
    }
  }, [isOpen, userProfile?.mailerXpApi]);

  const handleCreateListInWizard = async () => {
    if (!newListName.trim()) return;
    const key = userProfile?.mailerXpApi || inlineApiKey;
    if (!key) return;
    setCreatingList(true);
    setCreateListError(null);
    try {
      const res = await fetch('/api/mailerxp/lists/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: key, name: newListName.trim() })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to create list');
      }
      const created = data.list;
      setMailerXpLists(prev => [created, ...prev]);
      setForm(p => ({ ...p, mailerXpList: created.id }));
      setNewListName('');
      setShowCreateList(false);
    } catch (err) {
      setCreateListError(err.message);
    } finally {
      setCreatingList(false);
    }
  };

  const handleSaveInlineApiKey = async (key) => {
    if (!key || !key.trim()) return;
    const cleanKey = key.trim();
    setSavingApiKey(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user && user.email) {
        const emailClean = user.email.toLowerCase().trim();
        await fetch('/api/profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailClean, mailerXpApi: cleanKey })
        });
      }
      setInlineApiKey(cleanKey);
      await fetchMailerXpLists(cleanKey);
    } catch (err) {
      console.error('Failed to save MailerXP API key:', err);
    } finally {
      setSavingApiKey(false);
    }
  };

  // When coach result arrives, compute angle recommendation and seed angle plans
  useEffect(() => {
    if (!coachResult) return;
    const competition = deriveCompetitionLevel(coachResult);
    const rec = getAngleRecommendation(form.payout, competition);
    setAngleRec(rec);

    // Seed angle slots from coach-suggested angles (up to suggested count)
    const coachAngles = coachResult.angles || [];
    const seedCount = rec.suggested;
    const defaultFunnel = form.collectEmails ? 'Optin / Squeeze Page' : 'Curiosity Advertorial';
    const seeded = Array.from({ length: seedCount }, (_, i) => {
      const coachAngle = coachAngles[i];
      return {
        id: `angle-${Date.now()}-${i}`,
        name: coachAngle ? (coachAngle.name || '') : '',
        funnelType: defaultFunnel,
        status: 'queued',
        notes: coachAngle ? (coachAngle.hook || '') : '',
      };
    });
    setAnglePlans(seeded);
  }, [coachResult, form.collectEmails]);

  const startLoadingTimers = () => {
    setLoadingMsg('🔍 Coach is reading your offer...');
    const t1 = setTimeout(() => setLoadingMsg('🧠 Analyzing market angles...'), 5000);
    const t2 = setTimeout(() => setLoadingMsg('📊 Building your strategy...'), 10000);
    return [t1, t2];
  };

  // ─── Task 3: Use existing offer (skip scout if coachAnalysis cached) ─────
  const handleUseExistingOffer = async (offerId) => {
    if (!offerId) { setSelectedOfferId(null); return; }
    const offer = savedOffers.find(o => o.id === offerId);
    if (!offer) return;

    setSelectedOfferId(offerId);

    // Show confirm-network banner if this is a migrated offer
    if (offer.needsNetworkConfirm) {
      setNeedsNetworkConfirmOffer(offer);
    }

    // Pre-fill form from offer data
    setForm(p => ({
      ...p,
      name: offer.name || p.name,
      affiliateLink: offer.affiliateLink || p.affiliateLink,
      network: offer.network || p.network,
      payout: offer.payoutAmount ? `$${offer.payoutAmount}` : p.payout,
      payoutModel: offer.payoutModel || p.payoutModel,
      networkEpc: offer.offerEpc ? `$${offer.offerEpc}` : p.networkEpc,
      targetGeo: offer.targetGeo || p.targetGeo,
      offer_preview_url: offer.offerPreviewUrl || p.offer_preview_url,
      offer_description: offer.offer_description || offer.description || p.offer_description,
    }));

    // If offer already has cached coachAnalysis → skip scout, go straight to stage 2
    if (offer.coachAnalysis) {
      setCoachResult(offer.coachAnalysis);
      setWizardStage(2);
    }
    // Otherwise user can still click "Analyze Offer" to run scout (which will update the offer)
  };

  // ─── Step 1 → 2: Analyze Offer ───────────────────────────────────────────
  const handleAnalyzeOffer = async () => {

    const errs = {};
    if (!form.affiliateLink.trim()) {
      errs.affiliateLink = 'Affiliate link is required';
    } else if (!form.affiliateLink.toLowerCase().startsWith('http')) {
      errs.affiliateLink = 'Link must start with http/https';
    }
    if (!form.name.trim()) {
      errs.name = 'Campaign name is required';
    }
    const hasUrl = form.offer_preview_url?.trim()?.toLowerCase()?.startsWith('http');
    const hasDesc = form.offer_description?.trim() || form.niche?.trim();
    if (!hasUrl && !hasDesc) {
      errs.offer_preview_url = 'At least one of Offer Preview URL or Manual Description is required';
    }
    const cleanPayout = parseFloat(form.payout.replace(/[^0-9.]/g, ''));
    if (isNaN(cleanPayout) || cleanPayout <= 0) {
      errs.payout = 'Payout amount must be greater than 0';
    }
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setErrors({});
    setPipelineError(null);
    setPipelineLoading(true);

    let activeTimers = [];

    try {
      const apiKey = localStorage.getItem('ninja_api_key') || '';
      const primaryUrl = form.offer_preview_url || form.affiliateLink;

      let fetchedContent = '';

      // --- Call 1: Fetch and parse the offer preview URL ---
      if (primaryUrl && primaryUrl.toLowerCase().startsWith('http')) {
        setLoadingMsg('🔍 Fetching offer page...');
        try {
          const fetchResult = await fetchOfferContent(primaryUrl, apiKey);
          if (fetchResult && fetchResult.offer_text) {
            fetchedContent = fetchResult.offer_text;
          }
        } catch (fetchErr) {
          console.error('Failed to fetch offer page content:', fetchErr);
          setPipelineError('Could not fetch the offer URL. Check the link and try again.');
          setPipelineLoading(false);
          return;
        }
      }

      // --- Call 2: Call Claude API for Coach analysis ---
      setLoadingMsg('🧠 Coach is analyzing your offer...');
      
      // Start dynamic loading messages for coach analysis phase
      const t1 = setTimeout(() => setLoadingMsg('🧠 Analyzing market angles...'), 4000);
      const t2 = setTimeout(() => setLoadingMsg('📊 Building your strategy...'), 8000);
      activeTimers = [t1, t2];

      const payload = {
        ...form,
        campaignName: form.name,
        affiliateNetwork: form.network === 'Custom' ? form.customNetwork : form.network,
        affiliateLink: form.affiliateLink,
        payoutAmount: cleanPayout,
        payoutModel: form.payoutModel,
        targetGeo: form.targetGeo,
        offerEpc: form.networkEpc,
        offerPreviewUrl: form.offer_preview_url,
        offerDescription: form.offer_description || form.niche,
        offer_fetched_content: fetchedContent, // Pass the fetched content to skip scraping on server
        trafficSource: null,
      };

      const result = await runCampaignWizard(payload, apiKey, {
        analyzeOnly: true,
        onStatus: (msg) => console.log('[Wizard Status]', msg)
      });

      setCoachResult(result.coachAnalysis);
      setWizardStage(2);
    } catch (err) {
      console.error('Wizard pipeline failed:', err);
      setPipelineError(err.message || 'Coach analysis failed. Please try again.');
    } finally {
      activeTimers.forEach(clearTimeout);
      setPipelineLoading(false);
    }
  };

  // ─── Step 4 Angle Slot Helpers ────────────────────────────────────────────
  const addAngleSlot = () => {
    if (!angleRec || anglePlans.length >= angleRec.max) return;
    setAnglePlans(prev => [...prev, {
      id: `angle-${Date.now()}`,
      name: '',
      funnelType: FUNNEL_TYPES[0],
      status: 'queued',
      notes: '',
    }]);
  };

  const removeAngleSlot = (id) => {
    if (anglePlans.length <= 1) return;
    setAnglePlans(prev => prev.filter(a => a.id !== id));
  };

  const updateAngleSlot = (id, field, value) => {
    setAnglePlans(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  // ─── Launch Campaign (no page generation) ────────────────────────────────
  const handleLaunchCampaign = async () => {
    setSavingCampaign(true);
    try {
      // Mark first angle as active, rest as queued
      const finalAngles = anglePlans.map((a, i) => ({
        ...a,
        status: i === 0 ? 'active' : 'queued',
        clicks: 0,
        createdAt: new Date().toISOString(),
      }));

      // Build funnel architecture nodes for FunnelVisualizer
      const tSrc = selectedTrafficSource;
      const rowH = 260;
      const startY = 300 - ((finalAngles.length - 1) * rowH) / 2;

      // 1. Build Traffic Source nodes (one per angle stacked vertically on the left)
      const trafficNodes = finalAngles.map((a, i) => {
        const yVal = startY + i * rowH;
        return {
          id: `traffic-${i}`,
          type: 'traffic',
          x: 80,
          y: yVal,
          position: { x: 80, y: yVal },
          title: tSrc?.label || 'Traffic Source',
          label: tSrc?.label || 'Traffic',
          pageContent: { sourceProvider: 'custom', customSource: tSrc?.id || 'native' },
          data: {
            id: `traffic-${i}`,
            label: tSrc?.label || 'Traffic Source',
            angleId: a.id,
            angleName: a.name,
            angleStatus: a.status,
            initialLink: `https://${(form.customDomain && form.customDomain !== 'xpgo.link') ? form.customDomain : 'xpgo.link'}/u/${userProfile?.affiliateId || 'ninja_master'}/${form.slug || ''}`
          }
        };
      });

      // 2. Build Landing Page (angle type) nodes (one per angle stacked vertically in the middle)
      const angleNodes = finalAngles.map((a, i) => {
        const yVal = startY + i * rowH;
        const nodeType = a.funnelType === 'Custom HTML (Paste Code)'
          ? 'custom-html'
          : (a.funnelType === 'External Page (Self-Hosted)' ? 'external-page' : 'angle');
        return {
          id: `angle-${i}`,
          type: nodeType,
          x: 480,
          y: yVal,
          position: { x: 480, y: yVal },
          title: a.name || `Angle ${i + 1}`,
          label: a.name || `Angle ${i + 1}`,
          pageContent: {
            mailerXpList: form.collectEmails ? (form.mailerXpList || '') : '',
            mailerXpSegment: form.collectEmails ? (form.mailerXpSegment || '') : '',
            autoresponderProvider: form.autoresponderProvider || 'mailerxp',
            customForm: form.customForm || null,
            collectEmails: !!form.collectEmails,
            html: '',
            noindex: true,
            versions: [],
          },
          data: {
            id: `angle-${i}`,
            label: a.name || `Angle ${i + 1}`,
            angleId: a.id,
            angleName: a.name,
            funnelType: a.funnelType,
            angleStatus: a.status,
            notes: a.notes,
            mailerXpList: form.collectEmails ? (form.mailerXpList || '') : '',
          }
        };
      });

      // 3. Build exactly ONE shared Affiliate Offer node (centered on the right)
      const offerNode = {
        id: 'offer',
        type: 'offer',
        x: 880,
        y: 300,
        position: { x: 880, y: 300 },
        title: 'Affiliate Offer',
        label: 'Offer Page',
        campaignName: form.name,
        data: {
          id: 'offer',
          label: form.network === 'Custom' ? form.customNetwork : form.network,
          campaignName: form.name,
          affiliateLink: form.affiliateLink
        }
      };

      // 4. Connect nodes: traffic-i -> angle-i -> offer
      const connections = [
        ...finalAngles.map((a, i) => ({
          id: `edge-traffic-${i}-to-angle-${i}`,
          source: `traffic-${i}`,
          target: `angle-${i}`,
          type: 'animated'
        })),
        ...finalAngles.map((a, i) => ({
          id: `edge-angle-${i}-to-offer`,
          source: `angle-${i}`,
          target: 'offer',
          type: 'animated'
        }))
      ];

      const finalNodes = [...trafficNodes, ...angleNodes, offerNode];

      if (form.collectEmails) {
        const popupNode = {
          id: 'capture-popup',
          type: 'capture-popup',
          x: 480,
          y: startY + finalAngles.length * rowH,
          position: { x: 480, y: startY + finalAngles.length * rowH },
          title: 'Exit-Intent Popup',
          label: 'Capture Popup',
          pageContent: {
            mailerXpList: form.mailerXpList || '',
            mailerXpSegment: form.mailerXpSegment || '',
            autoresponderProvider: form.autoresponderProvider || 'mailerxp',
            customForm: form.customForm || null,
            headline: 'Wait! Get Your Free Guide Before You Go',
            subheadline: 'Enter your email below to get instant access.',
            ctaText: 'Claim Free Access Now →'
          },
          data: {
            id: 'capture-popup',
            label: 'Capture Popup',
            mailerXpList: form.mailerXpList || ''
          }
        };
        finalNodes.push(popupNode);
      }

      // ── Task 3+4: Seed per-angle pageSlug and adDestinationUrl ──────────
      const baseUsername = userProfile?.affiliateId || campaignData?.username || 'ninja_master';
      const baseSlug = form.slug || `campaign-${Date.now()}`;
      const cleanCustomDomain = (form.customDomain || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
      const hasCustomDomain = !!cleanCustomDomain && cleanCustomDomain !== 'xpgo.link';

      const anglesWithSlugs = finalAngles.map((a, i) => {
        const angleSlug = `${baseSlug}-a${i + 1}`;
        const adDestinationUrl = hasCustomDomain
          ? `https://${cleanCustomDomain}/${angleSlug}`
          : `https://xpgo.link/u/${baseUsername}/${angleSlug}`;
        const srcKey = tSrc?.id || 'mgid';
        const clickIdMacro = tSrc?.clickIdMacro || '{click_id}';
        const adDestinationUrlTemplate = `${adDestinationUrl}?source=${encodeURIComponent(srcKey)}&campaign=${encodeURIComponent(baseSlug)}&angle=${encodeURIComponent(a.id)}&click_id=${clickIdMacro}`;
        return {
          ...a,
          pageSlug: angleSlug,
          adDestinationUrl,
          adDestinationUrlTemplate,
          visits: 0,
          uniqueVisits: 0,
          optins: 0,
          sales: 0,
          revenue: 0,
          spend: 0,
          networkReportedClicks: 0,
        };
      });

      // ── Task 3: Auto-name "{offerName} — {geo} — {trafficSource}" ─────────
      const offerName = form.name?.trim() ||
        savedOffers.find(o => o.id === selectedOfferId)?.name ||
        form.niche?.trim() || 'Campaign';
      const geoStr = (form.targetGeo || 'US').split(',')[0].trim();
      const trafficLabel = tSrc?.label || 'Traffic';
      const autoName = `${offerName} — ${geoStr} — ${trafficLabel}`;
      const campaignName = form.name?.trim() ? form.name : autoName;

      const finalData = {
        ...campaignData,
        ...form,
        customDomain: hasCustomDomain ? cleanCustomDomain : 'xpgo.link',
        name: campaignName,
        network: form.network === 'Custom' ? form.customNetwork : form.network,
        username: baseUsername,
        createdAt: new Date().toISOString(),
        status: 'live',
        clicks: 0, conversions: 0, optins: 0,
        trafficSource: tSrc?.id || 'native',
        angle: anglesWithSlugs[0]?.name || 'advertorial',
        anglePlans: anglesWithSlugs,
        offerId: selectedOfferId || null,
        funnelArchitecture: {
          nodes: finalNodes,
          connections,
        },
        nodes: finalNodes,
        connections,
      };

      // ── Auth token for secured POST /api/campaigns ─────────────────────
      const token = await getAuth().currentUser?.getIdToken().catch(() => null);
      const res = await fetch('/api/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(finalData)
      });
      const data = await res.json();
      const saved = data.campaign || finalData;

      onLaunchSuccess(saved);
    } catch (e) {
      console.error('Failed to launch campaign:', e);
    } finally {
      setSavingCampaign(false);
    }
  };


  if (!isOpen) return null;

  // ─── Step Labels ─────────────────────────────────────────────────────────
  const STEPS = [
    { num: 1, label: 'OFFER DETAILS' },
    { num: 2, label: 'COACH ANALYSIS' },
    { num: 3, label: 'TRAFFIC SOURCE' },
    { num: 4, label: 'ANGLE PLANNING' },
    { num: 5, label: 'FUNNEL MAP' },
  ];

  const tierColors = { low: '#22c55e', medium: '#f97316', high: '#a78bfa' };

  return (
    <div className="fixed inset-0 bg-[#0d0f14]/95 z-[200] flex flex-col h-screen overflow-hidden font-mono text-slate-300">

      {/* Top Navbar */}
      <div className="h-16 border-b border-white/[0.08] px-8 flex items-center justify-between shrink-0 bg-[#13161d]">
        <div className="flex items-center space-x-3">
          <Rocket className="text-[#f97316] size-5 animate-pulse" />
          <h2 className="text-xs font-bold uppercase tracking-widest text-white">Campaign Building Coach</h2>
        </div>
        <button onClick={onClose} className="p-1 text-slate-500 hover:text-white hover:bg-white/5 rounded transition-colors">
          <X size={18} />
        </button>
      </div>

      {/* Steps Header */}
      <div className="bg-[#13161d] border-b border-white/[0.08] px-8 py-4 shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          {STEPS.map((s, idx) => {
            const done = wizardStage > s.num, active = wizardStage === s.num;
            return (
              <React.Fragment key={s.num}>
                <div className="flex items-center space-x-2">
                  <div className={`size-6 rounded-full flex items-center justify-center text-[10px] font-bold border transition-all ${
                    done ? 'bg-[#22c55e] border-[#22c55e] text-slate-950'
                    : active ? 'border-[#f97316] text-[#f97316] bg-[#f97316]/10'
                    : 'border-white/10 text-slate-600'
                  }`}>
                    {done ? <Check size={10} strokeWidth={3} /> : s.num}
                  </div>
                  <span className={`text-[8px] font-bold tracking-wider hidden sm:block ${
                    done ? 'text-slate-400' : active ? 'text-white' : 'text-slate-600'
                  }`}>{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-2 ${wizardStage > s.num ? 'bg-[#22c55e]/30' : 'bg-white/5'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Main Container */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-[#0d0f14] relative">

        {/* Loading Overlay */}
        {pipelineLoading && (
          <div className="absolute inset-0 bg-[#0d0f14]/90 z-[300] flex flex-col items-center justify-center space-y-6">
            <Loader2 className="animate-spin size-12 text-[#f97316] drop-shadow-[0_0_15px_rgba(249,115,22,0.3)]" />
            <div className="text-center space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#f97316] animate-pulse">Running Coach Analysis...</span>
              <h3 className="text-sm font-bold text-white tracking-wide">{loadingMsg}</h3>
            </div>
          </div>
        )}

        <div className="max-w-4xl mx-auto space-y-6">

          {/* Error Banner */}
          {pipelineError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-start gap-3">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <p className="text-xs font-bold font-sans">{pipelineError}</p>
                <button
                  type="button"
                  onClick={() => { setPipelineError(null); handleAnalyzeOffer(); }}
                  className="bg-red-500/25 hover:bg-red-500/35 border border-red-500/30 text-white font-bold text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              STAGE 1: Offer Details
          ════════════════════════════════════════════════════════════════ */}
          {wizardStage === 1 && (
            <div className="bg-[#13161d] border border-white/[0.08] rounded-2xl shadow-2xl p-8 space-y-6">
              <div>
                <h3 className="text-base font-bold text-white mb-1">🎯 Configure Your Affiliate Offer</h3>
                <p className="text-[11px] text-slate-500">Provide your offer details so the Coach can design your angle count, copy strategy, and bid targets.</p>
              </div>

              {/* ── Task 1/3: Use existing offer panel ────────────────────── */}
              {savedOffers.length > 0 && (
                <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-indigo-400">
                    <Library size={13} />
                    Use a Saved Offer
                  </div>
                  <div className="flex gap-3 items-center">
                    <select
                      value={selectedOfferId || ''}
                      onChange={e => handleUseExistingOffer(e.target.value || null)}
                      className="flex-1 bg-[#0d0f14] border border-indigo-500/30 rounded-lg px-3 py-2.5 text-xs text-white focus:outline-none focus:border-indigo-400 appearance-none font-bold cursor-pointer"
                    >
                      <option value="">— New offer —</option>
                      {savedOffers.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.name || o.affiliateLink} ({o.network || 'Unknown network'})
                          {o.coachAnalysis ? ' ✓ Cached' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedOfferId && (
                      <div className="text-[9px] text-emerald-400 font-black bg-emerald-500/10 border border-emerald-500/20 px-2 py-1.5 rounded-lg whitespace-nowrap">
                        {savedOffers.find(o => o.id === selectedOfferId)?.coachAnalysis
                          ? '✓ Scout cached — skipping to Stage 2'
                          : '↑ Fields pre-filled — review then Analyze'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Task 2: Network confirm banner (migrated offers) ──────── */}
              {needsNetworkConfirmOffer && (
                <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-1.5">
                    <p className="text-xs font-bold text-amber-300">Confirm Your Affiliate Network</p>
                    <p className="text-[10px] text-slate-400 font-sans leading-relaxed">
                      This offer was imported before network presets were available.
                      Please confirm the correct network below so compliance rules are accurate.
                    </p>
                    <button
                      type="button"
                      onClick={() => setNeedsNetworkConfirmOffer(null)}
                      className="text-[9px] font-black text-amber-400 hover:text-amber-300 underline"
                    >
                      I've confirmed the network below ✓
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Campaign Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Keto Weight Loss Supreme"
                    className={`w-full bg-[#0d0f14] border rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] font-bold text-white ${errors.name ? 'border-[#ef4444]' : 'border-white/[0.08]'}`}
                  />
                  {errors.name && <span className="text-[10px] text-[#ef4444] mt-1 block">{errors.name}</span>}
                </div>
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Affiliate Network</label>
                  <select
                    value={form.network}
                    onChange={e => setForm(p => ({ ...p, network: e.target.value, customNetwork: '' }))}
                    className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] cursor-pointer font-bold appearance-none text-white"
                  >
                    {['ClickBank', 'JVZoo', 'WarriorPlus', 'Digistore24', 'CPALead', 'MaxBounty', 'PropellerAds'].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                    <option value="Custom">Other / Custom Network</option>
                  </select>

                </div>
              </div>

              {form.network === 'Custom' && (
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Custom Network Name</label>
                  <input
                    type="text"
                    value={form.customNetwork}
                    onChange={e => setForm(p => ({ ...p, customNetwork: e.target.value }))}
                    placeholder="Enter network name..."
                    className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] font-bold text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Affiliate Link (Required)</label>
                <input
                  type="text"
                  value={form.affiliateLink}
                  onChange={e => setForm(p => ({ ...p, affiliateLink: e.target.value }))}
                  placeholder="Paste your affiliate link..."
                  className={`w-full bg-[#0d0f14] border rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] font-mono text-white ${errors.affiliateLink ? 'border-[#ef4444]' : 'border-[#f97316]'}`}
                />
                {errors.affiliateLink ? (
                  <span className="text-[10px] text-[#ef4444] mt-1.5 block"><AlertCircle size={12} className="inline mr-1" />{errors.affiliateLink}</span>
                ) : (
                  <span className="text-[9px] text-slate-500 mt-1.5 block">Get your link from the network dashboard before pasting here.</span>
                )}
              </div>

              <div>
                <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Offer Preview URL (Recommended)</label>
                <input
                  type="url"
                  value={form.offer_preview_url || ''}
                  onChange={e => setForm(p => ({ ...p, offer_preview_url: e.target.value }))}
                  placeholder="https://www.cpalead.com/offer/preview/..."
                  className={`w-full bg-[#0d0f14] border rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] font-sans text-white ${errors.offer_preview_url ? 'border-[#ef4444]' : 'border-white/[0.08]'}`}
                />
                {errors.offer_preview_url && <span className="text-[10px] text-[#ef4444] mt-1 block">{errors.offer_preview_url}</span>}
                <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed font-sans">
                  Paste the public offer page URL — your Coach reads it automatically so you don't have to describe the offer manually.
                </p>

                {form.offer_preview_url && form.offer_preview_url.trim().toLowerCase().startsWith('http') && (
                  <div className="mt-2.5 flex items-center gap-1.5 text-[10px] text-[#22c55e] bg-[#22c55e]/10 border border-[#22c55e]/20 px-3 py-1.5 rounded-md font-bold">
                    <span>✓ URL saved — Coach will analyze on submission</span>
                  </div>
                )}

                {(!form.offer_preview_url || !form.offer_preview_url.trim().toLowerCase().startsWith('http')) && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowManualDesc(!showManualDesc)}
                      className="text-[10px] text-[#f97316] hover:underline cursor-pointer flex items-center font-bold"
                    >
                      {showManualDesc ? '← Hide manual description' : 'No preview URL? Describe the offer instead →'}
                    </button>
                  </div>
                )}

                {showManualDesc && (!form.offer_preview_url || !form.offer_preview_url.trim().toLowerCase().startsWith('http')) && (
                  <div className="mt-3">
                    <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Describe the offer manually</label>
                    <textarea
                      value={form.offer_description}
                      onChange={e => setForm(p => ({ ...p, offer_description: e.target.value }))}
                      placeholder="e.g. Health supplement for weight loss. Strong VSL. Target: 35-65 seeking natural fat burners."
                      className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] h-24 resize-none font-sans text-white"
                    />
                  </div>
                )}
              </div>

              {/* Payout / Model / EPC Row */}
              <div className="grid grid-cols-3 gap-4 border-t border-white/[0.04] pt-5">
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Payout Amount</label>
                  <input
                    type="text"
                    value={form.payout}
                    onChange={e => setForm(p => ({ ...p, payout: e.target.value }))}
                    placeholder="e.g. $47.00"
                    className={`w-full bg-[#0d0f14] border rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] font-mono text-white ${errors.payout ? 'border-[#ef4444]' : 'border-white/[0.08]'}`}
                  />
                  {errors.payout && <span className="text-[10px] text-[#ef4444] mt-1 block">{errors.payout}</span>}
                </div>
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Payout Model</label>
                  <select
                    value={form.payoutModel}
                    onChange={e => setForm(p => ({ ...p, payoutModel: e.target.value }))}
                    className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] cursor-pointer font-bold appearance-none text-white"
                  >
                    {['CPS/Sale', 'CPA/Lead', 'CPL/Optin', 'CPC/Click', 'RevShare'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Offer EPC (Optional)</label>
                  <input
                    type="text"
                    value={form.networkEpc}
                    onChange={e => setForm(p => ({ ...p, networkEpc: e.target.value }))}
                    placeholder="e.g. $1.50"
                    className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] font-mono text-white"
                  />
                </div>
              </div>

              {/* GEO */}
              <div>
                <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Target GEO</label>
                <input
                  type="text"
                  value={form.targetGeo}
                  onChange={e => setForm(p => ({ ...p, targetGeo: e.target.value }))}
                  placeholder="e.g. US, UK, CA"
                  className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] text-white"
                />
              </div>

              {/* ── Branded Domain Selection ───────────── */}
              {userCustomDomains.length > 0 ? (
                <div className="pt-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-300 flex items-center gap-2">
                      <span className="text-base">🌐</span>
                      <span>Campaign Link Domain</span>
                    </label>
                    <span className="text-[9px] font-mono text-slate-500">
                      {userCustomDomains.length} configured in Settings
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {/* Custom Domains from Settings (Default domain listed first) */}
                    {userCustomDomains.map((dom, idx) => {
                      const isSelected = form.customDomain === dom;
                      const isDefaultDom = dom === defaultSettingsDomain || connectedDomains.find(d => d.hostname === dom)?.isDefault;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setForm(p => ({ ...p, customDomain: dom }))}
                          className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between ${
                            isSelected
                              ? 'bg-indigo-500/10 border-indigo-500 ring-1 ring-indigo-500'
                              : 'bg-[#0d0f14] border-white/[0.08] hover:border-white/20'
                          }`}
                        >
                          <div className="min-w-0 pr-2">
                            <div className="text-xs font-bold text-white flex items-center gap-1.5 truncate">
                              <span className="size-1.5 rounded-full bg-emerald-400 shrink-0"></span>
                              <span className="truncate font-mono">{dom}</span>
                              {isDefaultDom && (
                                <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                                  Default
                                </span>
                              )}
                            </div>
                            <div className="text-[9px] text-slate-500 mt-0.5">Your connected custom domain</div>
                          </div>
                          <div className={`size-4 rounded-full flex items-center justify-center border transition-all shrink-0 ${
                            isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-white/10'
                          }`}>
                            {isSelected && <Check size={10} strokeWidth={3} className="text-white" />}
                          </div>
                        </button>
                      );
                    })}

                    {/* Fallback Option: xpgo.link */}
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, customDomain: 'xpgo.link' }))}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between ${
                        (!form.customDomain && !defaultSettingsDomain) || form.customDomain === 'xpgo.link'
                          ? 'bg-[#f97316]/10 border-[#f97316] ring-1 ring-[#f97316]'
                          : 'bg-[#0d0f14] border-white/[0.08] hover:border-white/20'
                      }`}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="text-xs font-bold text-white flex items-center gap-1.5">
                          <span>⚡</span>
                          <span>xpgo.link</span>
                          {!defaultSettingsDomain && <span className="text-[9px] font-normal text-slate-400">(Fallback)</span>}
                        </div>
                        <div className="text-[9px] text-slate-500 mt-0.5">Shared AffiliateFlow link domain</div>
                      </div>
                      <div className={`size-4 rounded-full flex items-center justify-center border transition-all shrink-0 ${
                        ((!form.customDomain && !defaultSettingsDomain) || form.customDomain === 'xpgo.link') ? 'bg-[#f97316] border-[#f97316]' : 'border-white/10'
                      }`}>
                        {((!form.customDomain && !defaultSettingsDomain) || form.customDomain === 'xpgo.link') && <Check size={10} strokeWidth={3} className="text-white" />}
                      </div>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="pt-2 flex items-center justify-between text-[11px] text-slate-500">
                  <div className="flex items-center gap-1.5 font-sans">
                    <span>⚡</span>
                    <span>Link Domain: <strong className="text-slate-300 font-mono">xpgo.link</strong> (Default)</span>
                  </div>
                  <span className="text-[10px] text-slate-600 italic">Configure custom domains in Profile Settings</span>
                </div>
              )}

              {/* ── Email Collection & MailerXP Autoresponder ───────────── */}
              <div className="pt-4 border-t border-white/[0.06] space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-300 flex items-center gap-2">
                      <span className="text-base">📬</span>
                      <span>Email Collection & Autoresponder</span>
                    </label>
                    <span className="text-[9px] font-bold text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2.5 py-0.5 rounded-full">
                      MailerXP Integration
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 font-sans">
                    Choose whether this campaign should collect emails into a subscriber list before forwarding visitors to the affiliate offer.
                  </p>
                </div>

                {/* 2 Choice Cards: Yes vs No */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Option A: Yes */}
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, collectEmails: true }))}
                    className={`p-4 rounded-xl border text-left transition-all cursor-pointer space-y-2 relative ${
                      form.collectEmails
                        ? 'bg-purple-500/10 border-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.15)] ring-1 ring-purple-500'
                        : 'bg-[#0d0f14] border-white/[0.08] hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🎯</span>
                        <span className="text-xs font-bold text-white">Yes — Build Email List</span>
                      </div>
                      <div className={`size-4 rounded-full flex items-center justify-center border transition-all ${
                        form.collectEmails ? 'bg-purple-600 border-purple-600' : 'border-white/10'
                      }`}>
                        {form.collectEmails && <Check size={10} strokeWidth={3} className="text-white" />}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400 leading-relaxed font-sans">
                      Pre-sells the offer with an email opt-in gate / squeeze page & exit popup. Leads automatically sync to your <strong>MailerXP</strong> list.
                    </p>
                  </button>

                  {/* Option B: No */}
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, collectEmails: false }))}
                    className={`p-4 rounded-xl border text-left transition-all cursor-pointer space-y-2 relative ${
                      !form.collectEmails
                        ? 'bg-[#f97316]/10 border-[#f97316] shadow-[0_0_20px_rgba(249,115,22,0.15)] ring-1 ring-[#f97316]'
                        : 'bg-[#0d0f14] border-white/[0.08] hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">⚡</span>
                        <span className="text-xs font-bold text-white">No — Direct Pre-Sell Only</span>
                      </div>
                      <div className={`size-4 rounded-full flex items-center justify-center border transition-all ${
                        !form.collectEmails ? 'bg-[#f97316] border-[#f97316]' : 'border-white/10'
                      }`}>
                        {!form.collectEmails && <Check size={10} strokeWidth={3} className="text-white" />}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400 leading-relaxed font-sans">
                      Sends visitors directly from your advertorial or bridge page straight to the affiliate link without requiring an email address.
                    </p>
                  </button>
                </div>

                {/* If Yes: Show Autoresponder Type Selector & Config */}
                {form.collectEmails && (
                  <div className="bg-[#0d0f14] border border-purple-500/25 rounded-xl p-4 space-y-4 animate-in fade-in duration-200">
                    
                    {/* Autoresponder Provider Mode Tabs */}
                    <div className="flex items-center gap-2 p-1 bg-[#13161d] border border-white/[0.08] rounded-xl">
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, autoresponderProvider: 'mailerxp' }))}
                        className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
                          form.autoresponderProvider === 'mailerxp'
                            ? 'bg-purple-600 text-white shadow-md'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <span>📬</span>
                        <span>MailerXP (API Sync)</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, autoresponderProvider: 'custom_html' }))}
                        className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
                          form.autoresponderProvider === 'custom_html'
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <span>📝</span>
                        <span>HTML Form Embed (AWeber, etc.)</span>
                      </button>
                    </div>

                    {/* Option 1: MailerXP */}
                    {form.autoresponderProvider === 'mailerxp' && (
                      <div className="space-y-3.5 animate-in fade-in duration-150">
                        {!userProfile?.mailerXpApi && !inlineApiKey ? (
                          <div className="p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/20 space-y-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-purple-300 uppercase tracking-wider">Connect MailerXP API Key</span>
                              <span className="text-[9px] text-slate-400">Settings → API Key in MailerXP</span>
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={inlineApiKey}
                                onChange={e => setInlineApiKey(e.target.value)}
                                placeholder="Paste MailerXP API key (e.g. mxp_live_...)"
                                className="flex-1 bg-[#13161d] border border-white/[0.08] focus:border-purple-500 rounded-lg px-3 py-2 text-xs text-white font-mono outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => handleSaveInlineApiKey(inlineApiKey)}
                                disabled={savingApiKey || !inlineApiKey.trim()}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-[10px] font-black rounded-lg uppercase tracking-wider transition-all"
                              >
                                {savingApiKey ? 'Connecting...' : 'Connect'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between">
                              <label className="text-[9px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                <span>Destination MailerXP List</span>
                                <span className="size-1.5 rounded-full bg-emerald-400"></span>
                              </label>

                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => fetchMailerXpLists()}
                                  className="text-[9px] text-slate-400 hover:text-white font-bold uppercase tracking-wider flex items-center gap-1 transition-colors"
                                  title="Refresh lists from MailerXP"
                                >
                                  <span>🔄 Refresh</span>
                                </button>

                                {!showCreateList && (
                                  <button
                                    type="button"
                                    onClick={() => setShowCreateList(true)}
                                    className="text-[9px] text-purple-400 hover:text-purple-300 font-bold uppercase tracking-wider flex items-center gap-1 transition-colors"
                                  >
                                    <span>➕ Create New List</span>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Inline Create List Form */}
                            {showCreateList && (
                              <div className="p-3 rounded-xl bg-[#13161d] border border-purple-500/30 space-y-2 animate-in fade-in duration-150">
                                <div className="text-[10px] font-black text-purple-400 uppercase tracking-wider">Create New List in MailerXP</div>
                                <input
                                  type="text"
                                  value={newListName}
                                  onChange={e => setNewListName(e.target.value)}
                                  placeholder="e.g. Health & Fitness Leads"
                                  className="w-full bg-[#0d0f14] border border-white/[0.08] text-white text-xs px-3 py-2 rounded-lg focus:border-purple-500 outline-none"
                                  autoFocus
                                  onKeyDown={e => { if (e.key === 'Enter') handleCreateListInWizard(); }}
                                />
                                {createListError && <p className="text-[10px] text-rose-400">{createListError}</p>}
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={handleCreateListInWizard}
                                    disabled={creatingList || !newListName.trim()}
                                    className="flex-1 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-[9px] font-black rounded-lg uppercase tracking-wider transition-all flex items-center justify-center gap-1"
                                  >
                                    {creatingList ? 'Creating...' : 'Create & Select'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setShowCreateList(false); setNewListName(''); setCreateListError(null); }}
                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 text-[9px] font-black rounded-lg uppercase tracking-wider transition-all"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            {loadingMailerXp ? (
                              <div className="flex items-center gap-2 py-2 text-[10px] text-slate-400">
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-400" />
                                <span>Loading your MailerXP lists...</span>
                              </div>
                            ) : (
                              <select
                                value={form.mailerXpList || ''}
                                onChange={e => setForm(p => ({ ...p, mailerXpList: e.target.value }))}
                                className="w-full bg-[#13161d] border border-white/[0.08] focus:border-purple-500 rounded-lg px-4 py-2.5 text-xs text-white font-medium outline-none cursor-pointer"
                              >
                                <option value="">-- Select MailerXP List --</option>
                                {mailerXpLists.map(l => (
                                  <option key={l.id} value={l.id}>
                                    {l.name} {l.stats?.subscribers !== undefined ? `(${l.stats.subscribers} leads)` : ''}
                                  </option>
                                ))}
                              </select>
                            )}

                            {form.mailerXpList && (
                              <p className="text-[10px] text-emerald-400 flex items-center gap-1.5 font-medium">
                                <span>✓</span>
                                <span>All leads from this campaign will sync automatically to <strong>{mailerXpLists.find(l => l.id === form.mailerXpList)?.name || 'selected list'}</strong> in MailerXP.</span>
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Option 2: Custom HTML Form Embed (AWeber, GetResponse, etc.) */}
                    {form.autoresponderProvider === 'custom_html' && (
                      <div className="space-y-3 animate-in fade-in duration-150">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] font-black uppercase tracking-wider text-indigo-300 flex items-center gap-2">
                            <span>Autoresponder Form Embed HTML</span>
                          </label>
                          <span className="text-[9px] text-slate-500 font-mono">
                            AWeber, GetResponse, ActiveCampaign, etc.
                          </span>
                        </div>

                        <p className="text-[11px] text-slate-400 leading-relaxed font-sans">
                          Paste the raw HTML form embed code from your autoresponder service below. AffiliateFlow will automatically detect the submission URL, email input, and hidden tags.
                        </p>

                        <textarea
                          rows={4}
                          value={form.customFormHtml || ''}
                          onChange={e => {
                            const val = e.target.value;
                            setForm(p => ({ ...p, customFormHtml: val }));
                            handleParseCustomFormHtml(val);
                          }}
                          placeholder='<form action="https://www.aweber.com/scripts/addlead.pl" method="post">
  <input type="hidden" name="listname" value="awlist..." />
  <input type="email" name="email" />
  <input type="submit" value="Submit" />
</form>'
                          className="w-full bg-[#090b10] border border-white/[0.08] focus:border-indigo-500 rounded-xl p-3 text-xs text-white font-mono outline-none resize-none placeholder:text-slate-700 leading-relaxed"
                        />

                        {customFormError && (
                          <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs flex items-center gap-2">
                            <span>⚠️</span>
                            <span>{customFormError}</span>
                          </div>
                        )}

                        {parsedFormDetails && parsedFormDetails.success && (
                          <div className="p-3.5 rounded-xl bg-indigo-500/10 border border-indigo-500/25 space-y-2.5 animate-in fade-in duration-200">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                Form Elements Detected ({parsedFormDetails.provider})
                              </span>
                              <span className="text-[9px] font-mono text-slate-400 bg-black/40 px-2 py-0.5 rounded">
                                Method: {parsedFormDetails.method}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                              <div className="p-2 rounded-lg bg-[#090b10] border border-white/[0.05]">
                                <span className="text-[8px] uppercase tracking-wider text-slate-500 block">Form Action URL</span>
                                <span className="text-white truncate block text-[11px]" title={parsedFormDetails.action}>
                                  {parsedFormDetails.action}
                                </span>
                              </div>
                              <div className="p-2 rounded-lg bg-[#090b10] border border-white/[0.05]">
                                <span className="text-[8px] uppercase tracking-wider text-slate-500 block">Input Fields</span>
                                <span className="text-indigo-300 block text-[11px]">
                                  Email: <strong className="text-white">{parsedFormDetails.emailField}</strong> | Name: <strong className="text-white">{parsedFormDetails.nameField || 'none'}</strong>
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1">
                              <span>🏷️ Hidden Parameters: <strong className="text-white">{parsedFormDetails.hiddenCount} fields</strong> ({Object.keys(parsedFormDetails.hiddenFields).slice(0, 3).join(', ')}{parsedFormDetails.hiddenCount > 3 ? '...' : ''})</span>
                              <span className="text-emerald-400 font-bold">✓ Ready for live capture</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-white/[0.04] flex justify-end">
                <button
                  type="button"
                  onClick={handleAnalyzeOffer}
                  className="px-6 py-3.5 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold text-xs uppercase tracking-widest rounded-lg transition-colors cursor-pointer flex items-center gap-2"
                >
                  Analyze Offer <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              STAGE 2: Coach Analysis
          ════════════════════════════════════════════════════════════════ */}
          {wizardStage === 2 && coachResult && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div>
                <h3 className="text-base font-bold text-white mb-1">🤖 AI Coach Campaign Intelligence</h3>
                <p className="text-[11px] text-slate-500">Scouted parameters and marketing strategy are ready. Review before planning angles.</p>
              </div>

              {/* Offer Summary */}
              <div className="bg-[#13161d] border border-white/[0.08] rounded-2xl p-5 grid grid-cols-2 sm:grid-cols-4 gap-4 shadow-xl">
                <div className="space-y-1">
                  <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Brand</div>
                  <div className="text-sm font-bold text-white truncate">{coachResult.offer_summary?.brand || 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Conversion Action</div>
                  <div className="text-sm font-bold text-[#f97316] truncate">{coachResult.offer_summary?.conversion_action || 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Friction Steps</div>
                  <div className="text-sm font-bold text-white flex items-center gap-1.5">
                    <span>{coachResult.offer_summary?.friction_steps}</span>
                    {coachResult.offer_summary?.friction_steps > 1 && (
                      <span className="text-[8px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-black">HIGH FRICTION</span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Brand Tone</div>
                  <div className="text-sm font-bold text-slate-300 truncate">{coachResult.offer_summary?.brand_tone || 'Professional'}</div>
                </div>
              </div>

              {/* Compliance & Restrictions */}
              {(coachResult.offer_summary?.compliance_flags?.length > 0 || coachResult.offer_summary?.restrictions?.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {coachResult.offer_summary?.compliance_flags?.length > 0 && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex flex-col gap-2">
                      <div className="text-[10px] font-bold text-amber-500 uppercase tracking-widest flex items-center gap-2">
                        <AlertTriangle size={14} /> Compliance Warnings
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {coachResult.offer_summary.compliance_flags.map((flag, idx) => (
                          <span key={idx} className="bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2.5 py-1 rounded-md text-[10px] font-bold">
                            ⚠️ {flag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {coachResult.offer_summary?.restrictions?.length > 0 && (
                    <div className="bg-red-500/5 border border-red-500/15 rounded-xl p-4 flex flex-col gap-2">
                      <div className="text-[10px] font-bold text-red-400 uppercase tracking-widest flex items-center gap-2">
                        <AlertCircle size={14} /> Offer Restrictions
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {coachResult.offer_summary.restrictions.map((res, idx) => (
                          <span key={idx} className="bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded text-[9px] font-bold">{res}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Audience & Traffic */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#13161d] border border-white/[0.08] rounded-2xl p-6 space-y-4">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-white/5 pb-2">🎯 Audience Profile</div>
                  <div className="space-y-3">
                    <div>
                      <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Demographics</div>
                      <p className="text-xs text-white leading-relaxed mt-0.5">{coachResult.audience?.profile}</p>
                    </div>
                    <div>
                      <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Emotional State</div>
                      <span className="inline-block bg-[#f97316]/10 text-[#f97316] border border-[#f97316]/20 px-2 py-0.5 rounded-md text-[10px] font-bold mt-1">
                        🔥 {coachResult.audience?.emotional_state}
                      </span>
                    </div>
                    <div>
                      <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Core Desire</div>
                      <p className="text-xs text-slate-300 leading-relaxed mt-0.5">{coachResult.audience?.core_desire}</p>
                    </div>
                    <div>
                      <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Top Objection</div>
                      <p className="text-xs text-red-400/95 leading-relaxed mt-0.5">⚠️ {coachResult.audience?.top_objection}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#13161d] border border-white/[0.08] rounded-2xl p-6 space-y-4">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-white/5 pb-2">🚦 Traffic Recommendation</div>
                  <div className="space-y-4">
                    <div>
                      <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Primary Recommendation</div>
                      <div className="flex items-center gap-2 mt-1 bg-[#22c55e]/10 border border-[#22c55e]/20 px-3 py-2 rounded-xl">
                        <span className="text-base">🎯</span>
                        <span className="text-xs font-bold text-[#22c55e] uppercase">{coachResult.traffic?.primary}</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Strategy Rationale</div>
                      <p className="text-xs text-slate-300 leading-relaxed mt-0.5">{coachResult.traffic?.primary_reason}</p>
                    </div>
                    {coachResult.traffic?.alternative && (
                      <div>
                        <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Alternative Source</div>
                        <span className="text-[10px] text-slate-400 bg-white/5 px-2 py-1 rounded-md mt-1 inline-block">
                          🔄 {coachResult.traffic.alternative}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Budget Panel */}
              <div className="bg-[#13161d] border border-white/[0.08] rounded-2xl p-6 space-y-4">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-white/5 pb-2">💰 Bid & Budget Strategy</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                  <div className="space-y-1">
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Recommended Max CPC</div>
                    <div className="text-lg font-bold font-mono text-[#22c55e]">${parseFloat(coachResult.budget?.max_cpc || 0).toFixed(2)}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Daily Test Budget</div>
                    <div className="text-lg font-bold font-mono text-[#02B6FF]">${parseFloat(coachResult.budget?.daily_test_budget || 0).toFixed(2)}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Cut-at Click Threshold</div>
                    <div className="text-lg font-bold font-mono text-[#ef4444]">{coachResult.budget?.cut_at_clicks} clicks</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Scaling Trigger</div>
                    <div className="text-xs font-bold text-white truncate mt-1">{coachResult.budget?.scale_trigger}</div>
                  </div>
                </div>
              </div>

              {/* A/B Testing Priority */}
              <div className="bg-[#f59e0b]/5 border-l-4 border-[#f59e0b] p-5 rounded-r-2xl space-y-2">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#f59e0b] flex items-center gap-1.5">
                  <Sparkles size={12} /> A/B Testing Priority
                </div>
                <div>
                  <span className="text-[11px] font-bold text-white block mb-0.5">First Priority: {coachResult.ab_priority?.first_test}</span>
                  <p className="text-[11px] text-slate-400 leading-relaxed font-sans">{coachResult.ab_priority?.reason}</p>
                </div>
              </div>

              {/* ★ Coach Angle Count Recommendation Banner ★ */}
              {angleRec && (
                <div className="rounded-2xl border p-5 space-y-3" style={{
                  background: `${tierColors[angleRec.tier]}08`,
                  borderColor: `${tierColors[angleRec.tier]}30`,
                }}>
                  <div className="flex items-center gap-2">
                    <div className="size-8 rounded-full flex items-center justify-center text-base"
                      style={{ background: `${tierColors[angleRec.tier]}20` }}>🤖</div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: tierColors[angleRec.tier] }}>
                        Coach Recommendation
                      </div>
                      <div className="text-white font-bold text-sm mt-0.5">
                        Plan{' '}
                        <span className="font-black" style={{ color: tierColors[angleRec.tier] }}>
                          {angleRec.suggested} angle{angleRec.suggested !== 1 ? 's' : ''}
                        </span>
                        {' '}for this offer
                        <span className="text-slate-500 font-normal text-[10px] ml-2">(max {angleRec.max})</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed font-sans pl-10">{angleRec.reason}</p>
                  <div className="flex gap-2 pl-10">
                    {Array.from({ length: angleRec.max }, (_, i) => (
                      <div key={i} className="size-3 rounded-full" style={{
                        background: i < angleRec.suggested ? tierColors[angleRec.tier] : `${tierColors[angleRec.tier]}20`,
                        border: `1px solid ${tierColors[angleRec.tier]}50`,
                      }} />
                    ))}
                    <span className="text-[9px] text-slate-500 ml-1 mt-0.5">suggested / max slots</span>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-white/[0.04] flex justify-between">
                <button
                  type="button"
                  onClick={() => setWizardStage(1)}
                  className="px-5 py-3 border border-white/10 hover:border-white/20 rounded-lg text-slate-400 hover:text-white transition-colors text-xs uppercase font-bold cursor-pointer flex items-center gap-2"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  type="button"
                  onClick={() => setWizardStage(3)}
                  className="px-6 py-3.5 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold text-xs uppercase tracking-widest rounded-lg transition-colors cursor-pointer flex items-center gap-2"
                >
                  Choose Traffic Source <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              STAGE 3: Traffic Source
          ════════════════════════════════════════════════════════════════ */}
          {wizardStage === 3 && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div>
                <h3 className="text-base font-bold text-white mb-1">🚦 Select Your Traffic Source</h3>
                <p className="text-[11px] text-slate-500">Choose the primary traffic channel you'll run this campaign on. This determines the funnel architecture.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {TRAFFIC_SOURCES.map(src => {
                  const isSelected = selectedTrafficSource?.id === src.id;
                  const histData = accountHistory.sources[src.id];
                  return (
                    <button
                      key={src.id}
                      type="button"
                      onClick={() => setSelectedTrafficSource(src)}
                      className={`text-left p-5 rounded-2xl border transition-all cursor-pointer space-y-3 ${
                        isSelected
                          ? 'bg-[#f97316]/5 border-[#f97316] shadow-[0_0_20px_rgba(249,115,22,0.15)] ring-1 ring-[#f97316]'
                          : 'bg-[#13161d] border-white/[0.08] hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{src.emoji}</span>
                          <div>
                            <div className="text-xs font-bold text-white">{src.label}</div>
                            <div className="text-[9px] text-slate-500">{src.sub}</div>
                          </div>
                        </div>
                        <div className={`size-5 rounded-full flex items-center justify-center border transition-all shrink-0 ${
                          isSelected ? 'bg-[#f97316] border-[#f97316]' : 'border-white/10'
                        }`}>
                          {isSelected && <Check size={10} strokeWidth={3} className="text-white" />}
                        </div>
                      </div>

                      {/* Color accent line */}
                      <div className="h-px rounded-full" style={{ background: `${src.color}40` }} />

                      {/* Suggested angles for this source */}
                      <div className="space-y-1.5">
                        <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Best Angles</div>
                        {src.angles.slice(0, 3).map(a => (
                          <div key={a.id} className="flex items-center gap-2">
                            <div className="size-1 rounded-full shrink-0" style={{ background: src.color }} />
                            <span className="text-[9px] text-slate-400">{a.label}</span>
                          </div>
                        ))}
                      </div>

                      {/* Historical performance badge */}
                      {histData && (
                        <div className="bg-white/5 border border-white/5 rounded-lg px-3 py-2 flex items-center gap-2">
                          <CheckCircle2 size={10} className="text-[#22c55e] shrink-0" />
                          <span className="text-[9px] text-[#22c55e] font-bold">
                            Your data: {histData.count} campaigns · {(histData.avgCR * 100).toFixed(1)}% avg CR
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Coach Traffic Suggestion Callout */}
              {coachResult?.traffic?.primary && (
                <div className="bg-[#22c55e]/5 border border-[#22c55e]/20 rounded-xl p-4 flex items-start gap-3">
                  <span className="text-base shrink-0">🤖</span>
                  <div>
                    <div className="text-[10px] font-bold text-[#22c55e] uppercase tracking-wider">Coach Suggestion</div>
                    <p className="text-[11px] text-slate-300 mt-0.5 font-sans">
                      Based on your offer, the Coach recommends <strong className="text-white">{coachResult.traffic.primary}</strong>. {coachResult.traffic.primary_reason}
                    </p>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-white/[0.04] flex justify-between">
                <button
                  type="button"
                  onClick={() => setWizardStage(2)}
                  className="px-5 py-3 border border-white/10 hover:border-white/20 rounded-lg text-slate-400 hover:text-white transition-colors text-xs uppercase font-bold cursor-pointer flex items-center gap-2"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!selectedTrafficSource) return;
                    setWizardStage(4);
                  }}
                  disabled={!selectedTrafficSource}
                  className="px-6 py-3.5 bg-[#f97316] hover:bg-[#ea580c] disabled:bg-white/5 disabled:text-slate-600 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-widest rounded-lg transition-colors cursor-pointer flex items-center gap-2"
                >
                  Plan Angles <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              STAGE 4: Angle Planning
          ════════════════════════════════════════════════════════════════ */}
          {wizardStage === 4 && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-white mb-1">📐 Angle Planning</h3>
                  <p className="text-[11px] text-slate-500">Define your test angles. Each angle gets its own funnel path — no pages are built yet.</p>
                </div>
                {angleRec && (
                  <div className="text-right shrink-0 ml-4">
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Coach Max</div>
                    <div className="text-lg font-black" style={{ color: tierColors[angleRec.tier] }}>
                      {anglePlans.length} / {angleRec.max}
                    </div>
                  </div>
                )}
              </div>

              {/* Angle Slots */}
              <div className="space-y-4">
                {anglePlans.map((angle, idx) => (
                  <div
                    key={angle.id}
                    className="bg-[#13161d] border border-white/[0.08] rounded-2xl p-5 space-y-4 relative"
                  >
                    {/* Header row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="size-6 rounded-full bg-[#f97316]/15 border border-[#f97316]/30 flex items-center justify-center text-[10px] font-black text-[#f97316]">
                          {idx + 1}
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Angle {idx + 1}</span>
                      </div>

                      <div className="flex items-center gap-3">
                        {/* Status badge */}
                        <div className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/25 px-2.5 py-1 rounded-full">
                          <div className="size-1.5 rounded-full bg-amber-400 animate-pulse" />
                          <span className="text-[9px] font-bold text-amber-400 uppercase tracking-wider">Queued</span>
                        </div>
                        {/* Remove button */}
                        {anglePlans.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAngleSlot(angle.id)}
                            className="size-6 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors cursor-pointer"
                          >
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Angle Name + Funnel Type row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Angle Name</label>
                        <input
                          type="text"
                          value={angle.name}
                          onChange={e => updateAngleSlot(angle.id, 'name', e.target.value)}
                          placeholder="e.g. Doctor Discovery Hook"
                          className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] font-bold text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Funnel Type</label>
                        <select
                          value={angle.funnelType}
                          onChange={e => updateAngleSlot(angle.id, 'funnelType', e.target.value)}
                          className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-xs focus:outline-none focus:border-[#f97316] cursor-pointer font-bold appearance-none text-white"
                        >
                          {FUNNEL_TYPES.map(ft => (
                            <option key={ft} value={ft}>{ft}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Funnel type chip preview */}
                    {angle.funnelType && (() => {
                      const ftc = FUNNEL_TYPE_COLORS[angle.funnelType] || { bg: '#818cf8' };
                      return (
                        <div className="flex items-center gap-2">
                          <div className="px-3 py-1 rounded-full text-[9px] font-bold border"
                            style={{ background: `${ftc.bg}18`, borderColor: `${ftc.bg}40`, color: ftc.bg }}>
                            {angle.funnelType}
                          </div>
                          <span className="text-[9px] text-slate-500">funnel type selected</span>
                        </div>
                      );
                    })()}

                    {/* Notes */}
                    <div>
                      <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Notes (Optional)</label>
                      <textarea
                        value={angle.notes}
                        onChange={e => updateAngleSlot(angle.id, 'notes', e.target.value)}
                        placeholder="Target audience context, hooks in mind, any restrictions..."
                        rows={2}
                        className="w-full bg-[#0d0f14] border border-white/[0.08] rounded-lg px-4 py-3 text-[11px] focus:outline-none focus:border-[#f97316] resize-none font-sans text-slate-300"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Angle Button */}
              {angleRec && anglePlans.length < angleRec.max && (
                <button
                  type="button"
                  onClick={addAngleSlot}
                  className="w-full py-4 rounded-2xl border border-dashed border-white/10 hover:border-[#f97316]/40 text-slate-500 hover:text-[#f97316] flex items-center justify-center gap-2 transition-all cursor-pointer group"
                >
                  <Plus size={16} className="group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold uppercase tracking-wider">Add Another Angle</span>
                  <span className="text-[10px] text-slate-600">({anglePlans.length}/{angleRec.max} slots used)</span>
                </button>
              )}

              {/* Coach note */}
              <div className="bg-[#13161d] border border-white/[0.05] rounded-xl px-4 py-3 flex items-start gap-3">
                <span className="text-sm shrink-0">💡</span>
                <p className="text-[10px] text-slate-500 font-sans leading-relaxed">
                  <strong className="text-slate-400">No pages are built here.</strong> Angles are saved as queued plans. After launching, activate an angle from your Funnel Map to unlock page building for that angle only.
                </p>
              </div>

              <div className="pt-4 border-t border-white/[0.04] flex justify-between">
                <button
                  type="button"
                  onClick={() => setWizardStage(3)}
                  className="px-5 py-3 border border-white/10 hover:border-white/20 rounded-lg text-slate-400 hover:text-white transition-colors text-xs uppercase font-bold cursor-pointer flex items-center gap-2"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  type="button"
                  onClick={() => setWizardStage(5)}
                  className="px-6 py-3.5 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold text-xs uppercase tracking-widest rounded-lg transition-colors cursor-pointer flex items-center gap-2"
                >
                  Generate Funnel Map <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              STAGE 5: Funnel Map Preview
          ════════════════════════════════════════════════════════════════ */}
          {wizardStage === 5 && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div>
                <h3 className="text-base font-bold text-white mb-1">🗺️ Your Campaign Funnel Map</h3>
                <p className="text-[11px] text-slate-500">
                  One traffic source, {anglePlans.length} parallel angle{anglePlans.length !== 1 ? 's' : ''}, one shared offer. Pages are built after launch from your dashboard.
                </p>
              </div>

              {/* Funnel Map Visual */}
              <div className="bg-[#13161d] border border-white/[0.08] rounded-2xl p-6 overflow-hidden">
                <StaticFunnelMap
                  trafficSource={selectedTrafficSource}
                  anglePlans={anglePlans}
                  offerName={form.name}
                />
              </div>

              {/* Angle Summary Table */}
              <div className="bg-[#13161d] border border-white/[0.08] rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-2">
                  <Layers size={14} className="text-[#f97316]" />
                  <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">Angle Plan Summary</span>
                </div>
                <div className="divide-y divide-white/[0.05]">
                  {anglePlans.map((angle, idx) => {
                    const ftc = FUNNEL_TYPE_COLORS[angle.funnelType] || { bg: '#818cf8' };
                    const isFirst = idx === 0;
                    return (
                      <div key={angle.id} className="px-5 py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="size-7 rounded-full border flex items-center justify-center text-[10px] font-black shrink-0"
                            style={isFirst
                              ? { background: '#f97316', borderColor: '#f97316', color: '#fff' }
                              : { background: '#f9731615', borderColor: '#f9731630', color: '#f97316' }}>
                            {idx + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-white truncate">{angle.name || `Angle ${idx + 1}`}</div>
                            {angle.notes && <div className="text-[9px] text-slate-500 truncate mt-0.5">{angle.notes}</div>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="px-2 py-1 rounded-full text-[9px] font-bold border"
                            style={{ background: `${ftc.bg}18`, borderColor: `${ftc.bg}40`, color: ftc.bg }}>
                            {angle.funnelType}
                          </span>
                          {isFirst ? (
                            <span className="px-2 py-1 rounded-full text-[9px] font-bold bg-[#22c55e]/15 border border-[#22c55e]/30 text-[#22c55e]">
                              🟢 Will Activate
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-[9px] font-bold bg-amber-500/10 border border-amber-500/25 text-amber-400">
                              🟡 Queued
                            </span>
                          )}
                          {/* Build Page button — disabled */}
                          <div className="relative group">
                            <button
                              type="button"
                              disabled
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 text-slate-600 rounded-lg text-[9px] font-bold uppercase cursor-not-allowed"
                            >
                              <Lock size={9} />
                              Build Page
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 bg-[#1e2130] border border-white/10 text-slate-300 text-[9px] rounded-lg px-3 py-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 font-sans shadow-xl w-52">
                              {isFirst
                                ? '✅ Activate from Funnel Map after launch to build this page'
                                : '🔒 Set this angle Active from your Funnel Map to unlock page building'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Launch Info Banner */}
              <div className="bg-[#22c55e]/5 border border-[#22c55e]/20 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle2 size={16} className="text-[#22c55e] shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-[#22c55e] uppercase tracking-wider">Ready to Launch</div>
                  <p className="text-[11px] text-slate-300 leading-relaxed font-sans">
                    <strong className="text-white">Angle 1 ({anglePlans[0]?.name || 'First Angle'})</strong> will automatically become Active.
                    Go to your Funnel Map after launch, click <strong className="text-white">Build Page</strong> on the active angle to start the Page Builder Agent.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-white/[0.04] flex justify-between">
                <button
                  type="button"
                  onClick={() => setWizardStage(4)}
                  className="px-5 py-3 border border-white/10 hover:border-white/20 rounded-lg text-slate-400 hover:text-white transition-colors text-xs uppercase font-bold cursor-pointer flex items-center gap-2"
                >
                  <ArrowLeft size={14} /> Edit Angles
                </button>
                <button
                  type="button"
                  onClick={handleLaunchCampaign}
                  disabled={savingCampaign}
                  className="px-8 py-3.5 bg-[#f97316] hover:bg-[#ea580c] disabled:bg-white/5 disabled:text-slate-600 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-widest rounded-lg transition-colors cursor-pointer shadow-lg shadow-[#f97316]/15 flex items-center gap-2"
                >
                  {savingCampaign ? (
                    <><Loader2 size={14} className="animate-spin" /><span>Launching...</span></>
                  ) : (
                    <><Rocket size={14} /><span>Launch Campaign</span></>
                  )}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}